import {
  type StationPrivateConfig,
  type RecordEntityId,
  type StationPublicConfig,
  type ApiResponseError,
  P2Role,
} from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { cdlApi } from '@centric-os/app/src/api';
import type { AxiosResponse } from 'axios';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import { isEmpty } from 'lodash-es';

interface State extends StateTree {
  // States to handle multiple stations at the same time
  structuredStationPublicConfigs: Record<string, StationPublicConfig>;
  allStationPublicConfigs: Record<string, StationPublicConfig>;
  structuredStationPrivateConfigs: Record<string, StationPrivateConfig>;

  // Single station state
  stationPublicConfig: StationPublicConfig;
  stationPrivateConfig: StationPrivateConfig;
}

/**
 * Store for managing station configurations, both public and private.
 *
 * @param storeId - The unique identifier for the store.
 * @returns The station configuration store.
 */
export function useStationConfigStore(storeId = 'stationConfig') {
  const store = defineStore(storeId, {
    state: (): State => ({
      structuredStationPublicConfigs: null,
      allStationPublicConfigs: null,
      structuredStationPrivateConfigs: null,
      stationPublicConfig: null,
      stationPrivateConfig: null,
    }),
    actions: {
      /**
       * Retrieves the public configurations of multiple stations.
       *
       * @param ids - An array of station IDs.
       * @param refreshValues - Whether to refresh values even if cached data exists.
       * @returns A promise resolving when the data is fetched and stored.
       */
      async getAllStationPublicConfigs(
        ids: RecordEntityId[],
        refreshValues = false,
      ): Promise<void> {
        const structuredPublicConfigs: Record<string, StationPublicConfig> = {};
        await Promise.all(
          ids.map(async (id: RecordEntityId) => {
            if (refreshValues || !this.allStationPublicConfigs?.[id]) {
              const publicConfig = await this.getStationPublicConfig(id);
              // @ts-expect-error FIXME: type not assignable
              structuredPublicConfigs[id] = publicConfig;
              return;
            }
            structuredPublicConfigs[id] = this.allStationPublicConfigs?.[id];
          }),
        );
        this.structuredStationPublicConfigs = structuredPublicConfigs;
        this.allStationPublicConfigs = {
          ...this.allStationPublicConfigs,
          ...structuredPublicConfigs,
        };
      },

      /**
       * Retrieves the private configurations of multiple stations.
       *
       * @param ids - An array of station IDs.
       * @returns A promise resolving when the data is fetched and stored.
       */
      async getAllStationPrivateConfigs(ids: RecordEntityId[]): Promise<void> {
        const structuredPrivateConfigs: Record<string, StationPrivateConfig> = {};
        await Promise.all(
          ids.map(async (id: RecordEntityId) => {
            const privateConfig = await this.getStationPrivateConfig(id);
            // @ts-expect-error FIXME: type not assignable
            structuredPrivateConfigs[id] = privateConfig;
          }),
        );
        this.structuredStationPrivateConfigs = structuredPrivateConfigs;
      },

      /**
       * Fetches the station public config from the API.
       *
       * @param stationId - The ID of the station.
       * @returns A promise resolving to the station public config.
       */
      async fetchStationPublicConfig(stationId: RecordEntityId): Promise<StationPublicConfig> {
        const response = await cdlApi.get<
          StationPublicConfig,
          AxiosResponse<StationPublicConfig>,
          StationPublicConfig
        >(`config/public/${stationId}`);
        return response.data;
      },

      /**
       * Retrieves the station public config.
       *
       * @param stationId - The ID of the station.
       * @param updateStore - Whether to update the station store with the fetched config.
       * @returns A promise resolving to the station public config if `updateStore` is false.
       */
      async getStationPublicConfig(
        stationId: RecordEntityId,
        updateStore = false,
      ): Promise<StationPublicConfig | void> {
        const data = await this.fetchStationPublicConfig(stationId);

        if (updateStore) {
          this.stationPublicConfig = data;
          return;
        }

        return data;
      },

      /**
       * Fetches the station private config from the API.
       *
       * @param stationId - The ID of the station.
       * @returns A promise resolving to the station private config.
       */
      async fetchStationPrivateConfig(stationId: RecordEntityId): Promise<StationPrivateConfig> {
        const response = await cdlApi
          .get<StationPrivateConfig>(`config/${stationId}`)
          .catch((e: ApiResponseError) => {
            if (e.status === 404) {
              // @ts-expect-error FIXME: type '{ data: null; status: number
              const emptyData: AxiosResponse<StationPrivateConfig> = {
                data: null,
                status: 404,
                statusText: 'Not Found',
              };
              return emptyData;
            }
          });
        return response.data;
      },

      /**
       * Retrieves the station private config.
       *
       * @param stationId - The ID of the station.
       * @param updateStore - Whether to update the station store with the fetched config.
       * @returns A promise resolving to the station private config if `updateStore` is false.
       */
      async getStationPrivateConfig(
        stationId: RecordEntityId,
        updateStore = false,
      ): Promise<StationPrivateConfig | void> {
        const data = await this.fetchStationPrivateConfig(stationId);

        if (updateStore) {
          this.stationPrivateConfig = data;
          return;
        }

        return data;
      },

      /**
       * Save the station public configuration.
       *
       * @param {RecordEntityId} stationId - The ID of the station.
       * @param {Partial<StationPublicConfig>} payload - The updated public configuration data.
       * @param {boolean} updateState - Whether to update the store with the new config.
       * @returns A promise resolving when the update is complete.
       */
      async saveStationPublicConfig(
        stationId: RecordEntityId,
        payload: Partial<StationPublicConfig>,
        updateState = false,
      ): Promise<void> {
        const response = await cdlApi.put<StationPublicConfig>(`config/public/${stationId}`, {
          ...payload,
        });

        if (updateState) {
          this.stationPublicConfig = response.data;
        }
      },

      /**
       * Save the station private configuration.
       *
       * @param {RecordEntityId} stationId - The ID of the station.
       * @param {Partial<StationPrivateConfig>} payload - The updated private configuration data.
       * @param {boolean} updateState - Whether to update the store with the new config.
       * @returns A promise resolving when the update is complete.
       */
      async saveStationPrivateConfig(
        stationId: RecordEntityId,
        payload: Partial<StationPrivateConfig>,
        updateState = false,
      ): Promise<void> {
        if (isEmpty(payload)) {
          return;
        }
        await cdlApi.put<StationPrivateConfig>(`config/${stationId}`, payload);

        if (updateState) {
          this.stationPrivateConfig = payload;
        }
      },

      /**
       * Saves both private and public station configurations.
       * depending on the `isSiteConfig` flag.
       *
       * @param {string} id - The ID of the entity (station) for which the configurations are being saved.
       * @param {Partial<StationPrivateConfig>} privatePayload - A partial object containing the private configuration data to be saved.
       * @param {Partial<StationPublicConfig>} publicPayload - A partial object containing the public configuration data to be saved.
       * @returns {Promise<void>} A promise that resolves once both private and public configurations are successfully saved.
       */
      async saveStationConfig(
        id: string,
        privatePayload: Partial<StationPrivateConfig>,
        publicPayload: Partial<StationPublicConfig>,
        updateState = false,
      ): Promise<void> {
        // Create the list of promises to save both private and public configurations concurrently
        const promises = [this.saveStationPublicConfig(id, publicPayload, updateState)];

        // Set auth store
        const authStore = useAuthStore();

        // Check if the user has the role to save private configurations
        if (authStore.hasRole(P2Role.ADMIN) || authStore.hasRole(P2Role.SITE_OPERATOR)) {
          promises.push(this.saveStationPrivateConfig(id, privatePayload, updateState));
        }

        // Save both private and public configurations concurrently
        await Promise.all(promises);
      },
    },
    pagination: false,
  });
  return store();
}
